import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../api";
import { Camera, ShieldCheck, AlertTriangle, ShieldAlert } from "lucide-react";

/**
 * FaceRecheckGate — lightweight "face-still-here" check before starting a
 * subsequent section (Sales Pitch / Role Play) once the full ID + selfie
 * verification has already passed once for this assessment attempt.
 *
 * Behaviour:
 *  - Requests webcam only (no ID upload).
 *  - Candidate captures a single fresh selfie.
 *  - Selfie is compared to the reference selfie stored during the initial
 *    /proctor/id-verify pass. Server returns
 *    { passed, same_person, confidence, spoof_suspected, reasoning, ... }.
 *  - On pass → calls onReady().
 *  - On fail (same_person=false, low confidence) → shows a retry button;
 *    after 2 failures, offers a fallback to the full ProctorGate.
 *  - On fail with spoof_suspected=true (iter42) → shows a spoof-specific
 *    message, offers ONLY a retake (no "Confirm & start", no fallback).
 *    After 3 spoof strikes → calls /face-recheck/escalate-spoof which
 *    opens a security hold, then surfaces a hard-stop via onHardStop().
 *  - On 409 (no reference selfie on file) → auto-escalates to onFullFallback().
 */
const MAX_SPOOF_STRIKES = 3;

export default function FaceRecheckGate({ assignmentId, section, onReady, onFullFallback, onHardStop }) {
  const [phase, setPhase] = useState("consent"); // consent | capture | verifying | failed
  const [err, setErr] = useState("");
  const [stream, setStream] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [verdict, setVerdict] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [spoofStrikes, setSpoofStrikes] = useState(0);
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const startCamera = async () => {
    setErr("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setErr("This browser does not support webcam access. Please use Chrome, Edge or Safari on a laptop.");
      return;
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480 }, audio: false });
      setStream(s);
      setPhase("capture");
    } catch (e) {
      setErr(
        e?.name === "NotAllowedError"
          ? "Camera permission blocked. Enable camera access for this site and try again."
          : `Camera could not start (${e?.name || "unknown error"}).`
      );
    }
  };

  useEffect(() => {
    if (stream && phase === "capture" && !selfie && videoRef.current) {
      try {
        videoRef.current.srcObject = stream;
        const p = videoRef.current.play();
        if (p?.catch) p.catch(() => {});
      } catch (_) { /* ignore */ }
    }
  }, [stream, phase, selfie]);

  const capture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current, c = canvasRef.current;
    if (!v.videoWidth || !v.videoHeight) {
      setErr("Camera warming up — please wait a moment and try again.");
      return;
    }
    const w = 480, h = Math.round((v.videoHeight / v.videoWidth) * 480);
    c.width = w; c.height = h;
    c.getContext("2d").drawImage(v, 0, 0, w, h);
    setSelfie(c.toDataURL("image/jpeg", 0.85));
    setErr("");
  };

  // Escalate to a security hold after MAX_SPOOF_STRIKES consecutive
  // spoof-suspected rechecks. Mirrors the voice-mismatch hard-stop flow.
  const escalateSpoof = async (lastReasoning) => {
    try {
      await api.post("/participant/salescert/proctor/face-recheck/escalate-spoof", {
        assignment_id: assignmentId,
        section,
        strike_count: MAX_SPOOF_STRIKES,
        reasoning: lastReasoning || "",
      });
    } catch (e) {
      // Expected 423 from the escalate endpoint — the hold id is in detail.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      if (status === 423) {
        const match = /hold ([a-f0-9-]{8,})/i.exec(detail);
        const holdId = match ? match[1] : null;
        if (stream) stream.getTracks().forEach((t) => t.stop());
        onHardStop?.({ holdId, reason: "face_spoof" });
        return;
      }
      // Anything else is a surprise — fall back to full ID flow so the
      // candidate isn't left stuck with no path forward.
      setErr(formatApiError(e));
      onFullFallback?.();
    }
  };

  const verify = async () => {
    setPhase("verifying"); setErr("");
    try {
      const r = await api.post("/participant/salescert/proctor/face-recheck", {
        assignment_id: assignmentId,
        selfie_base64: selfie.split(",")[1],
        section,
      }, { timeout: 45000 });
      setVerdict(r.data);
      if (r.data.passed) {
        if (stream) stream.getTracks().forEach((t) => t.stop());
        setTimeout(() => onReady?.(), 300);
        return;
      }
      // Spoof-suspected gets its own retake flow; cannot be bypassed.
      if (r.data.spoof_suspected) {
        const nextStrikes = spoofStrikes + 1;
        setSpoofStrikes(nextStrikes);
        setAttempts((n) => n + 1);
        setPhase("failed");
        if (nextStrikes >= MAX_SPOOF_STRIKES) {
          await escalateSpoof(r.data.reasoning);
        }
        return;
      }
      // Normal mismatch (same_person=false or low confidence)
      setAttempts((n) => n + 1);
      setPhase("failed");
    } catch (e) {
      if (e?.response?.status === 409) {
        onFullFallback?.();
        return;
      }
      setErr(formatApiError(e));
      setPhase("failed");
      setAttempts((n) => n + 1);
    }
  };

  const retry = () => {
    setSelfie(null);
    setVerdict(null);
    setErr("");
    setVideoReady(false);
    setPhase("capture");
  };

  useEffect(() => () => {
    if (stream) stream.getTracks().forEach((t) => t.stop());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const spoofSuspected = Boolean(verdict?.spoof_suspected);
  const spoofRemaining = Math.max(0, MAX_SPOOF_STRIKES - spoofStrikes);

  return (
    <div className="max-w-2xl mx-auto fade-in" data-testid="face-recheck-gate">
      <canvas ref={canvasRef} className="hidden" />
      <div className="card p-8">
        <div className="overline mb-2 !text-[#EE2737]">QUICK IDENTITY RECHECK</div>
        <h1 className="font-heading font-black text-2xl mb-2">One quick face check before you begin</h1>
        <p className="text-[13px] text-[#525252] leading-relaxed mb-5">
          You already verified your VI Employee ID. For this section we just need a fresh live selfie
          to confirm it&apos;s still you at the keyboard. Takes 5 seconds — no ID card required.
        </p>

        {phase === "consent" && (
          <>
            {err && (
              <div className="bg-[#FEE2E2] border-2 border-[#DC2626] text-[#991B1B] px-4 py-3 mb-4" data-testid="face-recheck-cam-err">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
                  <div className="text-sm leading-relaxed">{err}</div>
                </div>
              </div>
            )}
            <button onClick={startCamera} className="btn btn-primary" data-testid="face-recheck-grant">
              <Camera size={14} /> {err ? "Retry · grant camera" : "Grant camera & continue"}
            </button>
          </>
        )}

        {(phase === "capture" || phase === "verifying" || phase === "failed") && (
          <>
            <div className="aspect-[4/3] bg-black mb-3 overflow-hidden max-w-sm">
              {selfie ? (
                <img src={selfie} alt="live selfie" className="w-full h-full object-cover" data-testid="face-recheck-preview" />
              ) : (
                <video
                  ref={videoRef}
                  muted
                  playsInline
                  autoPlay
                  onLoadedMetadata={() => setVideoReady(true)}
                  className="w-full h-full object-cover"
                  data-testid="face-recheck-video"
                />
              )}
            </div>

            {phase === "failed" && verdict && spoofSuspected && (
              <div
                className="bg-[#FEE2E2] border-2 border-[#DC2626] text-[#7F1D1D] text-sm px-4 py-3 mb-3 leading-relaxed"
                data-testid="face-recheck-spoof-failed"
              >
                <div className="flex items-start gap-2">
                  <ShieldAlert size={18} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <strong>This face check did not pass.</strong> Your selfie looked like it may
                    have been a photo of a screen or a printed image rather than a live person.
                    Please remove any phone/photo from in front of the camera and retake with your
                    actual face in good lighting.
                    <div className="text-[11px] text-[#525252] mt-1.5 uppercase tracking-wider font-semibold">
                      Attempt {spoofStrikes} of {MAX_SPOOF_STRIKES}
                      {spoofRemaining > 0 && <span> · {spoofRemaining} retake{spoofRemaining === 1 ? "" : "s"} left before your attempt is paused for admin review</span>}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {phase === "failed" && verdict && !spoofSuspected && (
              <div className="bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] text-sm px-4 py-3 mb-3 leading-relaxed" data-testid="face-recheck-failed">
                <strong>Face recheck failed.</strong> {verdict.reasoning}
                <div className="text-[11px] text-[#525252] mt-1">
                  Confidence: {Math.round((verdict.confidence || 0) * 100)}%. Please face the camera in good lighting and retake.
                </div>
              </div>
            )}
            {err && <div className="bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] text-sm px-3 py-2 mb-3">{err}</div>}

            <div className="flex flex-wrap gap-2">
              {!selfie && (
                <button
                  onClick={capture}
                  disabled={!videoReady || phase === "verifying"}
                  className="btn btn-secondary"
                  data-testid="face-recheck-capture"
                >
                  <Camera size={14} /> {videoReady ? "Capture selfie" : "Warming up camera…"}
                </button>
              )}
              {selfie && phase !== "verifying" && (
                <>
                  <button onClick={retry} className="btn btn-ghost !text-xs" data-testid="face-recheck-retake">
                    Retake
                  </button>
                  {/* Suppress "Confirm & start" entirely when the last verdict was a spoof suspicion.
                      The candidate must retake until the check passes OR strikes exhaust and the
                      SecurityHardStop takes over. */}
                  {!spoofSuspected && (
                    <button onClick={verify} className="btn btn-primary" data-testid="face-recheck-verify">
                      <ShieldCheck size={14} /> Confirm &amp; start
                    </button>
                  )}
                  {spoofSuspected && (
                    <button
                      onClick={verify}
                      className="btn btn-primary"
                      data-testid="face-recheck-verify-spoof-retake"
                      disabled={spoofStrikes >= MAX_SPOOF_STRIKES}
                    >
                      <Camera size={14} /> Submit retake ({spoofRemaining} left)
                    </button>
                  )}
                </>
              )}
              {phase === "verifying" && (
                <button disabled className="btn btn-primary opacity-70">
                  <ShieldCheck size={14} /> Verifying…
                </button>
              )}
              {/* The full-fallback option is only offered for ordinary mismatches, never when a
                  spoof is suspected — otherwise a spoofed attempt could bypass the whole flow by
                  re-doing the full ID step. */}
              {attempts >= 2 && !spoofSuspected && onFullFallback && (
                <button
                  onClick={onFullFallback}
                  className="btn btn-ghost !text-xs"
                  data-testid="face-recheck-fallback"
                  title="Fall back to the full ID + selfie flow"
                >
                  Redo full ID verification instead
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
